import { useRef, useState } from 'react';
import { FileVideo, ImageIcon, Loader2, Upload, X } from 'lucide-react';
import { useAssetUpload, validateFile, type AssetKind, type UploadedImage } from '../useAssetUpload';

const ACCEPT: Record<AssetKind, string> = {
  image: 'image/jpeg,image/png,image/webp',
  video: 'video/mp4,video/quicktime',
};

// Reusable asset uploader for the Feed + Story slots (D3.4 — image OR video).
// Empty → drag/drop + click-to-browse; uploading → progress bar; uploaded →
// preview (img or <video>) with Replace / Remove. Errors surface inline with a
// retry affordance. `kind` drives the accept filter, validation, and preview.
export function AssetUploader({
  value,
  onChange,
  accountId,
  aspectGuidance,
  label,
  kind = 'image',
  onFileSelected,
}: {
  value: UploadedImage | null;
  onChange: (next: UploadedImage | null) => void;
  accountId: string;
  aspectGuidance: string;
  label?: string;
  kind?: AssetKind;
  // D3.4b — fires with the LOCAL File the moment a VALID file is chosen, before
  // (and in parallel with) the GCS upload. Video slots wire this to the
  // thumbnail auto-extraction hook; invalid files never fire it.
  onFileSelected?: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, uploading, progress, error, reset } = useAssetUpload(accountId);
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const disabled = !accountId || uploading;
  const isVideo = kind === 'video';

  async function handleFile(file: File) {
    setFileName(file.name);
    if (onFileSelected && validateFile(file, kind) === null) onFileSelected(file);
    const result = await upload(file, kind);
    if (result) onChange(result);
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = ''; // allow re-selecting the same file
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  const browse = () => inputRef.current?.click();

  return (
    <div className="max-w-md">
      {label && <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT[kind]}
        onChange={onInputChange}
        className="hidden"
        data-testid="asset-file-input"
      />

      {/* Uploaded preview */}
      {value && !uploading && (
        <div className="space-y-2">
          <div className="overflow-hidden rounded-md border border-slate-200">
            {isVideo ? (
              <video src={value.imageUrl} controls className="block max-h-64 w-full bg-slate-900 object-contain" />
            ) : (
              <img src={value.imageUrl} alt="Creative preview" className="block max-h-64 w-full object-contain bg-slate-50" />
            )}
          </div>
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 truncate text-slate-500">
              {isVideo ? <FileVideo className="h-3.5 w-3.5 shrink-0" /> : <ImageIcon className="h-3.5 w-3.5 shrink-0" />}
              <span className="truncate">{fileName ?? (isVideo ? 'Uploaded video' : 'Uploaded image')}</span>
            </span>
            <span className="flex shrink-0 gap-3">
              <button type="button" onClick={browse} disabled={disabled} className="font-medium text-kyck-700 hover:underline disabled:opacity-40">
                Replace
              </button>
              <button
                type="button"
                onClick={() => {
                  setFileName(null);
                  reset();
                  onChange(null);
                }}
                className="inline-flex items-center gap-1 font-medium text-slate-500 hover:text-slate-700"
              >
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            </span>
          </div>
        </div>
      )}

      {/* Uploading */}
      {uploading && (
        <div className="rounded-md border border-slate-200 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" /> Uploading {fileName ?? 'image'}…
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-kyck-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Empty drop zone */}
      {!value && !uploading && (
        <button
          type="button"
          onClick={browse}
          disabled={disabled}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={[
            'flex w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-8 text-center text-sm transition-colors',
            disabled
              ? 'cursor-not-allowed border-slate-200 text-slate-300'
              : dragOver
                ? 'border-kyck-400 bg-kyck-50 text-kyck-700'
                : 'border-slate-300 text-slate-500 hover:border-slate-400',
          ].join(' ')}
        >
          <Upload className="h-5 w-5" />
          <span>
            {accountId ? (
              <>
                <span className="font-medium text-slate-700">Click to upload</span> or drag a{' '}
                {isVideo ? 'video' : 'image'} here
              </>
            ) : (
              'Pick an account first'
            )}
          </span>
          <span className="text-xs text-slate-400">
            {isVideo ? 'MP4 · up to 1GB' : 'JPEG, PNG, or WebP'} · {aspectGuidance}
          </span>
        </button>
      )}

      {error && (
        <p className="mt-2 text-sm text-destructive">
          {error}{' '}
          <button type="button" onClick={browse} className="font-medium underline">
            Try again
          </button>
        </p>
      )}
    </div>
  );
}
